import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ClientSelectionService } from '../components/clients/client-selection.service';
import { RreLink, RreLinkBody, RreLinksResult, UnlinkedRre } from '../models/rrelinking.model';
import { OfficeOption } from '../models/offices.model';

@Injectable({
  providedIn: 'root',
})
export class RrelinkingService {
  private readonly endpoint = 'rre-linking/links';

  constructor(
    private http: HttpClient,
    private clientSelectionService: ClientSelectionService
  ) {}

  getRreLinks(
    claimOfficeID?: string,
    rre?: string,
    skip: number = 0,
    count: number = 0,
    sortBy?: string,
    sortDir?: 'asc' | 'desc',
    search?: string
  ): Observable<RreLinksResult> {
    let params = new HttpParams()
      .set('skip', String(skip))
      .set('count', String(count))
      .set('sortBy', sortBy ?? '')
      .set('sortDir', sortDir ?? '');

    const trimmedSearch = String(search ?? '').trim();
    if (trimmedSearch) {
      params = params.set('search', trimmedSearch);
    }

    const trimmedClaimOfficeID = String(claimOfficeID ?? '').trim();
    if (trimmedClaimOfficeID) {
      params = params.set('claimOfficeID', trimmedClaimOfficeID);
    }

    const trimmedRre = String(rre ?? '').trim();
    if (trimmedRre) {
      params = params.set('rre', trimmedRre);
    }

    return this.http.get<unknown>(this.getUrl(), { headers: this.getHeaders(), params }).pipe(
      map((response) => {
        const rows = this.extractRows(response);
        const links = rows.map((row) => this.normalizeRow(row));
        const totalCount = this.extractTotalCount(response, links.length);
        return { links, totalCount };
      })
    );
  }

  getOffices(): Observable<OfficeOption[]> {
    return this.http
      .get<unknown>(`${environment.apiUrl}offices`, { headers: this.getHeaders() })
      .pipe(
        map((response: any) => {
          const list: any[] = Array.isArray(response?.offices) ? response.offices : [];
          return list.map(o => ({
            officeKey: String(o.OfficeKey ?? o.officeKey ?? ''),
            claimOfficeId: Number(o.ClaimOfficeID ?? o.claimOfficeID ?? o.claimOfficeId ?? ''),
            mailingName: String(o.MailingName ?? o.mailingName ?? ''),
          }));
        })
      );
  }

  getUnlinkedRREs(claimOfficeId: number): Observable<UnlinkedRre[]> {
    return this.http
      .get<unknown>(
        `${environment.apiUrl}${this.endpoint}/unlinkedRREs/${claimOfficeId}`,
        { headers: this.getHeaders() }
      )
      .pipe(
        map((response: any) => {
          const list: any[] = Array.isArray(response?.rres) ? response.rres : [];
          return list.map(r => ({
            rre: String(r.Rre ?? r.rre ?? ''),
            rreName: String(r.RreName ?? r.rreName ?? r.RREName ?? ''),
          }));
        })
      );
  }

  addRreLink(body: RreLinkBody): Observable<boolean> {
    return this.http
      .post<unknown>(this.getUrl(), body, { headers: this.getHeaders() })
      .pipe(map((response: any) => response?.success === true));
  }

  deleteRreLink(body: RreLinkBody): Observable<boolean> {
    return this.http
      .delete<unknown>(this.getUrl(), { headers: this.getHeaders(), body })
      .pipe(map((response: any) => response?.success === true));
  }

  private getUrl(): string {
    return `${environment.apiUrl}${this.endpoint}`;
  }

  private getHeaders(): HttpHeaders {
    const clientDbName = this.clientSelectionService.getSelectedClientDbName();

    return new HttpHeaders({
      ClientDBName: clientDbName,
    });
  }

  private extractRows(response: unknown): any[] {
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray((response as any)?.links)) {
      return (response as any).links;
    }

    if (Array.isArray((response as any)?.rreLinks)) {
      return (response as any).rreLinks;
    }

    if (Array.isArray((response as any)?.data)) {
      return (response as any).data;
    }

    if (Array.isArray((response as any)?.items)) {
      return (response as any).items;
    }

    return [];
  }

  private extractTotalCount(response: unknown, fallback: number): number {
    const r = response as any;
    if (typeof r?.totalCount === 'number') return r.totalCount;
    if (typeof r?.TotalCount === 'number') return r.TotalCount;
    if (typeof r?.total === 'number') return r.total;
    return fallback;
  }

  private normalizeRow(row: any): RreLink {
    return {
      Office: this.readValue(row, ['ClaimOfficeID', 'claimOfficeID', 'Office', 'office']),
      OfficeName: this.readValue(row, ['OfficeName', 'officeName']),
      RRE: this.readValue(row, ['RRE', 'rre']),
      RREName: this.readValue(row, ['RREName', 'rreName']),
    };
  }

  private readValue(source: any, keys: string[]): string {
    for (const key of keys) {
      if (source?.[key] !== undefined && source?.[key] !== null) {
        return String(source[key]);
      }
    }

    return '';
  }
}
